/**
 * PR-C5 (ADR-0039, 2026-05-31) — Confirmation dialog «Обновить данные из КП?».
 *
 * Показывается `ContractFormContainer`, когда родной КП изменился после
 * последнего ответа на промпт (`quote.updatedAt > max(lastQuoteUpdatedAt,
 * quotePromptAckAt)`). «Да» → пересев родного блока из КП; «Нет» → подавить
 * модалку для этой версии КП (ссылка «↻ Обновить из КП» остаётся, Вариант 2).
 *
 * Паттерн mirror `DeleteContractDialog` / `ContractNumConflictDialog`: overlay
 * `bg-black/50`, карточка `bg-canvas` r20, role=dialog + aria-modal +
 * aria-labelledby, Esc/overlay = «Нет» (dismiss). Вертикальные кнопки
 * (UI-правило — горизонтальные ряды запрещены). Design-токены P-038..P-048.
 *
 * a11y: initial focus на «Нет» (safer default — «Да» теряет ручные правки
 * родного блока, защита от accidental Enter). Готового shared `<ConfirmDialog/>`
 * нет (B-049 рефактор отложен) — реализован независимо, как остальные модалки.
 */

import { useEffect, useRef } from 'react';

export interface ReseedFromQuoteDialogProps {
  isOpen: boolean;
  /** «Да» — пересев родного блока из КП. */
  onConfirm: () => void;
  /** «Нет» / Esc / overlay — подавить модалку (бамп quotePromptAckAt). */
  onCancel: () => void;
}

export function ReseedFromQuoteDialog({ isOpen, onConfirm, onCancel }: ReseedFromQuoteDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) cancelButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="reseed-from-quote-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reseed-from-quote-dialog-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="reseed-from-quote-dialog-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 19, margin: '0 0 24px' }}
          data-testid="reseed-from-quote-dialog-title"
        >
          Обновить данные из КП?
        </h2>
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={onConfirm}
            aria-label="Да, обновить из КП"
            data-testid="reseed-from-quote-confirm"
            className={
              'w-full font-bold bg-accent text-ink ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Да, обновить
          </button>
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            aria-label="Нет, оставить как есть"
            data-testid="reseed-from-quote-cancel"
            className={
              'w-full font-bold bg-card text-ink border-[1.5px] border-accent ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Нет, оставить
          </button>
        </div>
      </div>
    </div>
  );
}
